import {
  type CreateOperatingCostInvoiceV1Dto,
  OperatingCostInvoicesService,
  type OperatingCostInvoiceV1Dto,
  type PatchOperatingCostInvoiceV1Dto,
} from '@/openapi';

import { useOperatingCostInvoiceLinesConverter } from '../components/operationcostinvoicelines/hooks/useOperatingCostInvoiceLines';
import type { OperatingCostInvoiceLineItemSimple } from '../components/operationcostinvoicelines/operatingCostInvoiceTypes';
import type { OperatingCostInvoiceSimple } from '../operatingCostInvoiceTypes';

export function useOperatingCostInvoices() {
  // Get single by id
  const fetchOperatingCostInvoice = async (id: string) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindOneV1(id);
  };

  // List all
  const fetchOperatingCostInvoices = async () => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindAllV1();
  };

  // Create
  const createOperatingCostInvoice = async (data: CreateOperatingCostInvoiceV1Dto) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1CreateV1(data);
  };

  // Patch (partial update)
  const updateOperatingCostInvoice = async (id: string, data: PatchOperatingCostInvoiceV1Dto) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1UpdatePartialV1(id, data);
  };

  // Delete single
  const deleteOperatingCostInvoice = async (id: string) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1RemoveOneV1(id);
  };

  // Delete many
  const deleteOperatingCostInvoices = async (ids: string[]) => {
    // API expects a DeleteManyRequest shape (usually { values: string[] })
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1RemoveManyV1({
      values: ids,
    });
  };

  /** --- Queries by relation --- */

  // List by PointOfSale
  const fetchOperatingCostInvoicesByPointOfSale = async (pointOfSaleId: string) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindManyByPointOfSaleIdV1(
      pointOfSaleId,
    );
  };

  // List by Contract
  const fetchOperatingCostInvoicesByContract = async (contractId: string) => {
    return OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindManyByContractIdV1(
      contractId,
    );
  };

  return {
    // CRUD
    fetchOperatingCostInvoice,
    fetchOperatingCostInvoices,
    createOperatingCostInvoice,
    updateOperatingCostInvoice,
    deleteOperatingCostInvoice,
    deleteOperatingCostInvoices,
    // Queries by relation
    fetchOperatingCostInvoicesByPointOfSale,
    fetchOperatingCostInvoicesByContract,
  };
}

export function useOperatingCostInvoicesConverter() {
  const {
    convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto,
    convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList,
  } = useOperatingCostInvoiceLinesConverter();

  const convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple = (
    data: OperatingCostInvoiceV1Dto,
  ): OperatingCostInvoiceSimple => {
    const {
      ticket: _ticket,
      createdAt: _createdAt,
      modifiedAt: _modifiedAt,
      contract,
      pointOfSale,
      ...res
    } = data;
    return {
      ...res,
      pointOfSaleId: pointOfSale?.id || '',
      pointOfSaleName: pointOfSale?.name || '',
      contractId: contract?.id || '',
      contractName: contract?.name || '',
      lineItems: convertOperatingCostInvoiceLineItemDtoListToOperatingCostInvoiceLineItemSimpleList(
        data.lineItems ?? [],
      ),
      ticketId: data.ticket?.id,
    };
  };

  const convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList = (
    data: OperatingCostInvoiceV1Dto[],
  ): OperatingCostInvoiceSimple[] => {
    return data.map(convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple);
  };

  const convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto = (
    data: OperatingCostInvoiceSimple,
    lineItems: OperatingCostInvoiceLineItemSimple[],
  ): CreateOperatingCostInvoiceV1Dto => {
    const { version: _version, id: _id, areas: _areas, ...res } = data;

    return {
      ...res,
      lineItems: lineItems.map((item) =>
        convertOperatingCostInvoiceLineItemSimpleToCreateOperatingCostInvoiceLineItemDto(item),
      ),
    };
  };

  const convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto = (
    data: OperatingCostInvoiceSimple,
  ): PatchOperatingCostInvoiceV1Dto => {
    const {
      id: _id,
      areas: _areas,
      lineItems: _lineItems,
      pointOfSaleName: _pointOfSaleName,
      contractName: _contractName,
      ...res
    } = data;

    return {
      ...res,
    };
  };

  // Fetch single and convert to Simple
  const fetchAndConvertToSimple = async (id: string): Promise<OperatingCostInvoiceSimple> => {
    // Fetch DTO and convert to Simple
    const dto = await OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindOneV1(id);
    return convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple(dto);
  };

  // Fetch list and convert to Simple[]
  const fetchAllAndConvertToSimple = async (): Promise<OperatingCostInvoiceSimple[]> => {
    const list = await OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindAllV1();
    return convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(list);
  };

  // Fetch by POS and convert to Simple[]
  const fetchByPosAndConvertToSimple = async (
    pointOfSaleId: string,
  ): Promise<OperatingCostInvoiceSimple[]> => {
    const list =
      await OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindManyByPointOfSaleIdV1(
        pointOfSaleId,
      );
    return convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(list);
  };

  // Fetch by Contract and convert to Simple[]
  const fetchByContractAndConvertToSimple = async (
    contractId: string,
  ): Promise<OperatingCostInvoiceSimple[]> => {
    const list =
      await OperatingCostInvoicesService.operatingCostInvoiceControllerV1FindManyByContractIdV1(
        contractId,
      );
    return convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList(list);
  };

  const fetchLatestByContractAndConvertToSimple = async (
    contractId: string,
  ): Promise<OperatingCostInvoiceSimple | null> => {
    const list = await fetchByContractAndConvertToSimple(contractId);
    if (!list.length) return null;

    const sorted = [...list].sort((a, b) => {
      // issueDate kann string oder Date sein – wir casten hart auf Date
      const aTime = a.issueDate ? new Date(a.issueDate).getTime() : 0;
      const bTime = b.issueDate ? new Date(b.issueDate).getTime() : 0;
      return bTime - aTime; // newest first
    });

    return sorted[0] ?? null;
  };

  return {
    convertOperatingCostInvoiceDtoToOperatingCostInvoiceSimple,
    convertOperatingCostInvoiceDtoListToOperatingCostInvoiceSimpleList,
    convertOperatingCostInvoiceSimpleToCreateOperatingCostInvoiceDto,
    convertOperatingCostInvoiceSimpleToUpdateOperatingCostInvoiceDto,

    // Optional convenience wrappers
    fetchAndConvertToSimple,
    fetchAllAndConvertToSimple,
    fetchByPosAndConvertToSimple,
    fetchByContractAndConvertToSimple,
    fetchLatestByContractAndConvertToSimple,
  };
}
